import { useEffect } from 'react';
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { AirportRef, Leg } from '@flugwettrennen/shared';

export interface MapPoint {
  iata: string;
  name: string;
  lat: number;
  lon: number;
}

/** Strecke, die gerade geflogen wird und noch kein `Leg` ist. */
export interface PendingRoute {
  from: AirportRef;
  to: AirportRef;
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0]!, 4);
    } else if (points.length > 1) {
      map.fitBounds(points, { padding: [30, 30] });
    }
  }, [map, points]);
  return null;
}

export function RouteMap({
  legs,
  start,
  pending,
}: {
  legs: Leg[];
  start?: MapPoint | null;
  /** Laufender Flug: wird gestrichelt gezeichnet, das Ziel bleibt offen. */
  pending?: PendingRoute | null;
}) {
  const airports = new Map<string, MapPoint>();
  if (start) airports.set(start.iata, start);
  for (const l of legs) {
    airports.set(l.from.iata, { iata: l.from.iata, name: l.from.name, lat: l.from.lat, lon: l.from.lon });
    airports.set(l.to.iata, { iata: l.to.iata, name: l.to.name, lat: l.to.lat, lon: l.to.lon });
  }
  if (pending) {
    airports.set(pending.from.iata, {
      iata: pending.from.iata,
      name: pending.from.name,
      lat: pending.from.lat,
      lon: pending.from.lon,
    });
  }
  // Das Ziel des laufenden Fluges ist noch nicht erreicht – eigener Marker,
  // damit es sich von den tatsächlich angeflogenen Flughäfen unterscheidet.
  const pendingTarget = pending && !airports.has(pending.to.iata) ? pending.to : null;

  const points: [number, number][] = [...airports.values()].map((a) => [a.lat, a.lon]);
  if (pendingTarget) points.push([pendingTarget.lat, pendingTarget.lon]);
  const center: [number, number] = points[0] ?? [20, 0];

  return (
    <div className="map">
      <MapContainer center={center} zoom={2} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {legs.map((l, i) => (
          <Polyline
            key={i}
            positions={[
              [l.from.lat, l.from.lon],
              [l.to.lat, l.to.lon],
            ]}
            pathOptions={{ color: '#a9750a', weight: 2.5, opacity: 0.8 }}
          />
        ))}
        {pending && (
          <Polyline
            positions={[
              [pending.from.lat, pending.from.lon],
              [pending.to.lat, pending.to.lon],
            ]}
            pathOptions={{ color: '#a9750a', weight: 2.5, opacity: 0.7, dashArray: '6 8' }}
          />
        )}
        {[...airports.values()].map((a) => (
          <CircleMarker
            key={a.iata}
            center={[a.lat, a.lon]}
            radius={5}
            pathOptions={{ color: '#12161c', fillColor: '#f5b301', fillOpacity: 1, weight: 2 }}
          >
            <Tooltip>
              {a.iata} · {a.name}
            </Tooltip>
          </CircleMarker>
        ))}
        {pendingTarget && (
          <CircleMarker
            center={[pendingTarget.lat, pendingTarget.lon]}
            radius={5}
            pathOptions={{ color: '#a9750a', fillColor: '#ffffff', fillOpacity: 1, weight: 2, dashArray: '3 3' }}
          >
            <Tooltip>
              {pendingTarget.iata} · {pendingTarget.name}
            </Tooltip>
          </CircleMarker>
        )}
        <FitBounds points={points.length ? points : [center]} />
      </MapContainer>
    </div>
  );
}
